import React, {useEffect} from 'react';
import type {AddFormType} from './types';
import {
  type GuestSchemaContextProps,
  type GuestSchemaStateOptions,
  type IdentityVerificationDetails,
} from './GuestSchemaContext';
import {useQuery, keepPreviousData} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';
import {API, MAX_RETRIES_REQUESTS_NUMBER, PAYLOAD_FIELDS_NAMES} from '@chekin/api';
import {GuestSchemaV2, IDENTITY_VERIFICATION_TYPES, QUERY_CACHE_KEYS} from '@chekin/core';

const StaleTime = 60_000;
const CacheTime = 5 * 60_000;
const SCHEMA_REFETCH_DEBOUNCE_MS = 250;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = React.useState(value);

  React.useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);

  return debounced;
}

export function useGuestSchemaState({
  reservationId,
  enabled = true,
  onSchemaError,
}: GuestSchemaStateOptions): GuestSchemaContextProps {
  const [isGuestSchemaLoaded, setIsGuestSchemaLoaded] = React.useState(false);
  const [birthCountry, setBirthCountry] = React.useState<string>();
  const [nationality, setNationality] = React.useState<string>();
  const [documentType, setDocumentType] = React.useState<string>();
  const {t, i18n} = useTranslation();
  const [documentExpeditionCountry, setDocumentExpeditionCountry] =
    React.useState<string>();
  const [arrivalCountry, setArrivalCountry] = React.useState<string>();
  const [arrivalDistrict, setArrivalDistrict] = React.useState<string>();
  const [nextDestinationCountry, setNextDestinationCountry] = React.useState<string>();
  const [residenceCountry, setResidenceCountry] = React.useState<string>();
  const [residenceProvince, setResidenceProvince] = React.useState<string>();
  const [birthDate, setBirthDate] = React.useState<string>();
  const [hasPhone, setHasPhone] = React.useState<boolean>(false);
  const [citizenship, setCitizenship] = React.useState<string>();
  const [isLeaderGuest, setIsLeaderGuest] = React.useState<boolean | undefined>(
    undefined,
  );
  const [invoiceLeaderType, setInvoiceLeaderType] = React.useState<string>();
  const [recipientCodeType, setRecipientCodeType] = React.useState<string>();

  const schemaKey = React.useMemo(
    () =>
      JSON.stringify([
        nationality,
        citizenship,
        birthCountry,
        arrivalCountry,
        arrivalDistrict,
        residenceCountry,
        residenceProvince,
        nextDestinationCountry,
        documentExpeditionCountry,
        documentType,
        birthDate,
        hasPhone,
        isLeaderGuest,
        i18n.language,
        invoiceLeaderType,
        recipientCodeType,
        reservationId,
      ]),
    [
      nationality,
      citizenship,
      birthCountry,
      arrivalCountry,
      arrivalDistrict,
      residenceCountry,
      residenceProvince,
      nextDestinationCountry,
      documentExpeditionCountry,
      documentType,
      birthDate,
      hasPhone,
      isLeaderGuest,
      i18n.language,
      invoiceLeaderType,
      recipientCodeType,
      reservationId,
    ],
  );
  const debouncedSchemaKey = useDebouncedValue(schemaKey, SCHEMA_REFETCH_DEBOUNCE_MS);

  const {
    data: guestSchema,
    error: guestSchemaError,
    isLoading: isGuestSchemaLoading,
    isFetching: isGuestSchemaFetching,
    refetch: refetchGuestSchema,
  } = useQuery<GuestSchemaV2>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [QUERY_CACHE_KEYS.formFields, debouncedSchemaKey],
    queryFn: () =>
      API.guest.getSchema({
        [PAYLOAD_FIELDS_NAMES.reservation_id]: reservationId as string,
        [PAYLOAD_FIELDS_NAMES.birth_place_country]: birthCountry,
        [PAYLOAD_FIELDS_NAMES.nationality]: nationality,
        [PAYLOAD_FIELDS_NAMES.document_expedition_country]: documentExpeditionCountry,
        [PAYLOAD_FIELDS_NAMES.next_destination_country]: nextDestinationCountry,
        [PAYLOAD_FIELDS_NAMES.arrived_from_country]: arrivalCountry,
        [PAYLOAD_FIELDS_NAMES.arrived_from_district]: arrivalDistrict,
        [PAYLOAD_FIELDS_NAMES.residence_country]: residenceCountry,
        [PAYLOAD_FIELDS_NAMES.residence_province]: residenceProvince,
        [PAYLOAD_FIELDS_NAMES.document_type]: documentType,
        [PAYLOAD_FIELDS_NAMES.citizenship]: citizenship,
        [PAYLOAD_FIELDS_NAMES.birth_date]: birthDate,
        [PAYLOAD_FIELDS_NAMES.phone]: hasPhone,
        [PAYLOAD_FIELDS_NAMES.is_leader]: isLeaderGuest,
        [PAYLOAD_FIELDS_NAMES.force_show_leaders_fields]: isLeaderGuest,
        [PAYLOAD_FIELDS_NAMES.invoice_leader_type]: invoiceLeaderType,
        [PAYLOAD_FIELDS_NAMES.recipient_code_type]: recipientCodeType,
      }),
    enabled: enabled && Boolean(reservationId),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: StaleTime,
    gcTime: CacheTime,
    retry: MAX_RETRIES_REQUESTS_NUMBER,
    placeholderData: keepPreviousData,
  });

  const isGuestSchemaInitialLoading = isGuestSchemaLoading && !isGuestSchemaLoaded;

  const hasGuestSchemaFailed = Boolean(guestSchemaError) && !guestSchema;

  useEffect(() => {
    if ((guestSchema || guestSchemaError) && !isGuestSchemaLoaded) {
      setIsGuestSchemaLoaded(true);
    }
  }, [guestSchema, guestSchemaError, isGuestSchemaLoaded]);

  const onSchemaErrorRef = React.useRef(onSchemaError);
  onSchemaErrorRef.current = onSchemaError;

  useEffect(() => {
    if (guestSchemaError) {
      onSchemaErrorRef.current?.(guestSchemaError);
    }
  }, [guestSchemaError]);

  const formFieldsData = guestSchema?.guest_fields;
  const agreementClauses = guestSchema?.agreement_clauses;

  const reservationDocumentsList = React.useMemo(() => {
    return guestSchema?.iv?.document_choices.map(dc => ({
      ...dc,
      label: t(dc.label),
    }));
  }, [t, guestSchema?.iv?.document_choices]);

  const identityVerificationDetails = React.useMemo<IdentityVerificationDetails>(() => {
    const ivDetails = guestSchema?.iv;
    const isOCR =
      ivDetails?.verification_type === IDENTITY_VERIFICATION_TYPES.ocr ||
      ivDetails?.verification_type === IDENTITY_VERIFICATION_TYPES.document;
    const isBiomatch =
      ivDetails?.verification_type === IDENTITY_VERIFICATION_TYPES.biomatch;
    const isIvExempted = Boolean(ivDetails?.is_iv_exempted);

    return {
      enabled: (isOCR || isBiomatch) && !isIvExempted,
      required: Boolean(ivDetails?.required),
      documents: ivDetails?.document_choices || [],
      biomatch: isBiomatch,
      ocr: isOCR,
      isIvExempted,
      isGuestRegisterBlocked: Boolean(ivDetails?.is_guest_register_blocked),
      underageAllowedAge: ivDetails?.is_guest_under_16_allow_to_register ? 16 : null,
    };
  }, [guestSchema?.iv]);

  const transformFormFieldsArrayToObject = React.useCallback((): AddFormType => {
    if (!formFieldsData) return {};
    return formFieldsData?.reduce((accumulator, field) => {
      return {...accumulator, [field.name]: field};
    }, {});
  }, [formFieldsData]);

  const formFields = React.useMemo<AddFormType>(
    () => transformFormFieldsArrayToObject(),
    [transformFormFieldsArrayToObject],
  );

  return React.useMemo<GuestSchemaContextProps>(
    () => ({
      formFields,
      identityVerificationDetails,
      setBirthCountry,
      setArrivalCountry,
      setArrivalDistrict,
      setNextDestinationCountry,
      setNationality,
      setDocumentType,
      setDocumentExpeditionCountry,
      setResidenceCountry,
      setResidenceProvince,
      setCitizenship,
      setBirthDate,
      setHasPhone,
      setIsLeaderGuest,
      setInvoiceLeaderType,
      setRecipientCodeType,
      isGuestSchemaLoaded,
      isGuestSchemaLoading,
      isGuestSchemaFetching,
      isGuestSchemaInitialLoading,
      hasGuestSchemaFailed,
      refetchGuestSchema,
      reservationDocumentsList,
      agreementClauses,
    }),
    [
      formFields,
      identityVerificationDetails,
      isGuestSchemaLoaded,
      isGuestSchemaLoading,
      isGuestSchemaFetching,
      isGuestSchemaInitialLoading,
      hasGuestSchemaFailed,
      refetchGuestSchema,
      reservationDocumentsList,
      agreementClauses,
    ],
  );
}
